import DOMPurify from 'dompurify'
import appConfig from '../../../../src/app-config'

/**
 * Sanitize and validate html string, handling edge cases and preventing XSS attacks.
 * @param {string} htmlContent - The input html to sanitize.
 * @returns {string} - A sanitized and validated html content, or an empty string if invalid.
 */
export const sanitizeHtml = (htmlContent) => {
  return DOMPurify.sanitize(htmlContent, {
    ADD_TAGS: appConfig.sanitizeHtmlAddTags,
    ADD_ATTR: appConfig.sanitizeHtmlAddAtts,
    CUSTOM_ELEMENT_HANDLING: {
      tagNameCheck: () => true,
      attributeNameCheck: () => true,
      allowCustomizedBuiltInElements: true, // customized built-ins are allowed
    },
  })
}

export const DEFAULT_URL = 'about:blank'

/**
 * Sanitize and validate a URL, handling edge cases and preventing XSS attacks.
 * @param {string} url - The input URL to sanitize.
 * @returns {string} - A sanitized and validated URL, or an empty string if invalid.
 */
export const sanitizeUrl = (url) => {
  try {
    if (typeof url !== 'string') {
      return url
    }

    const sanitizedAnchor = sanitizeHtml(`<a href="${url}"></a>`)

    const div = document.createElement('div')
    div.innerHTML = sanitizedAnchor

    return div.getElementsByTagName('a')[0].href || DEFAULT_URL
  } catch (e) {
    return url
  }
}

/**
 * Sanitize and validate an image URL, handling edge cases and preventing XSS attacks.
 * @param {string} url - The input URL to sanitize.
 * @returns {string} - A sanitized and validated URL, or an empty string if invalid.
 */
export const sanitizeImgUrl = (url) => {
  try {
    if (typeof url !== 'string') {
      return url
    }

    const sanitizedAnchor = sanitizeHtml(`<img src="${url}" />`)

    const div = document.createElement('div')
    div.innerHTML = sanitizedAnchor

    return div.getElementsByTagName('img')[0].src || DEFAULT_URL
  } catch (e) {
    return url
  }
}

export const stripHtml = (html) => {
  const div = document.createElement('div')
  div.innerHTML = html
  return div.textContent || div.innerText || ''
}

/**
 * Extracts HTML strings of all <span> elements with class 'input__math' and a 'data-latex' attribute from the given string.
 *
 * @param {string} html - The HTML string to search for math latex spans.
 * @returns {string[]} An array of outerHTML strings for each matching <span> element.
 */
export const extractMathLatexSpanHtmls = (html) => {
  const tempContainer = document.createElement('div')
  tempContainer.innerHTML = html

  const spanMatches = []
  tempContainer
    .querySelectorAll('span.input__math[data-latex]')
    .forEach((n) => spanMatches.push(n.outerHTML))

  return spanMatches
}

/**
 * Replaces all <span class="input__math" data-latex> elements in the given HTML string with a replacement value or function result.
 *
 * @param {string} html - The HTML string containing math latex spans to replace.
 * @param {string|function} replacement - The replacement string or a function that receives the span's outerHTML and returns a replacement string.
 * @returns {string} The resulting text content after replacements.
 */
export const replaceMathLatexSpans = (html, replacement) => {
  const tempContainer = document.createElement('div')
  tempContainer.innerHTML = html

  const mathSpans = Array.from(
    tempContainer.querySelectorAll('span.input__math[data-latex]')
  )

  if (mathSpans.length > 0) {
    mathSpans.forEach((span) => {
      let replacementContent

      if (typeof replacement === 'function') {
        replacementContent = replacement(span.outerHTML)
      } else {
        replacementContent = String(replacement)
      }

      const tempReplacementDiv = document.createElement('div')
      tempReplacementDiv.innerHTML = replacementContent

      // This correctly inserts actual HTML nodes, not encoded text.
      while (tempReplacementDiv.firstChild) {
        span.parentNode.insertBefore(tempReplacementDiv.firstChild, span)
      }
      span.parentNode.removeChild(span)
    })

    return tempContainer.innerHTML
  }

  return html
}

export const getElementCenterPosition = (element) => {
  if (element) {
    const elementDimensions = element.getBoundingClientRect()
    return {
      x: elementDimensions.left + elementDimensions.width / 2,
      y: elementDimensions.top + elementDimensions.height / 2,
    }
  }
  return null
}
